import { request, requestWithMsg, MsgResult } from './request';

// 材料类别 (两级树). material.type1/type2 只能来源于本列表的启用项 (后端校验),
// 数据源头是飞书《材料分类-线上最终版》, 之后由"类别管理" tab (仅管理员) 维护.

export interface MaterialCategory {
    id: number;
    uuid: string;
    parentId: number;
    /** 类别编码: 业务键, 建后不可改, 同父级下唯一; material.type1/type2、喷墨参数 type1/type2 存的就是它 */
    categoryCode: string;
    nameZh: string;
    /** 英文名 (展示用, 可改名; 关联一律用 categoryCode) */
    nameEn: string;
    /** 材质编码前缀 (二级类别用, 材-02): 新建材料按 前缀+顺延编号 自动生成 xcode */
    codePrefix?: string;
    /** 图标 URL (OSS 相对路径, 展示用 imgSrc 补前缀) */
    icon?: string;
    /** 材料使用说明链接 (教程 URL, 仅一级分类维护): 该类材料未单独配说明时的默认跳转地址 */
    usageDocUrl?: string;
    /** 使用说明展示标题 (与链接成对, 渲染成「标题 + 外链」) */
    usageDocTitle?: string;
    /** 剔除机型 (仅二级分类维护): device.family 机型码, 这些机型的 Studio 材料库侧栏不展示本分类; 空 = 全机型可见 */
    excludedDeviceFamilies?: string[];
    /** 手动排序权重 (与设备管理同义, 升序, 0=未设置排最后; 2026-08-14 由 sort 改名) */
    sno: number;
    isEnabled: boolean;
    createdBy: number;
    updatedBy: number;
    remark?: string;
    createdAt?: number;
    updatedAt?: number;
}

export interface MaterialCategoryNode extends MaterialCategory {
    children: MaterialCategory[];
}

const BASE = '/v1';

/** 类别树 (一级 + children). admin 维护页传 includeDisabled=true 取全集. */
export function listMaterialCategories(
    opts: { includeDisabled?: boolean } = {},
): Promise<MaterialCategoryNode[]> {
    return request<MaterialCategoryNode[]>(`${BASE}/material-categories`, {
        query: { includeDisabled: opts.includeDisabled ? 'true' : '' },
    });
}

let treeCache: Promise<MaterialCategoryNode[]> | null = null;

/** 类别树的模块级缓存 (小静态数据, 展示侧取中文名用; 失败不缓存). */
export function loadMaterialCategoriesCached(): Promise<MaterialCategoryNode[]> {
    if (!treeCache) {
        treeCache = listMaterialCategories().catch(() => {
            treeCache = null;
            return [] as MaterialCategoryNode[];
        });
    }
    return treeCache;
}

// 写接口: 统一走审批流 (message 含"已提交审批", data={taskCode}).
export function createMaterialCategory(payload: {
    parentId?: number;
    /** 类别编码 (建后不可改); 空 = 后端取 nameEn */
    categoryCode?: string;
    nameZh: string;
    nameEn: string;
    icon?: string;
    codePrefix?: string;
    usageDocUrl?: string;
    usageDocTitle?: string;
    excludedDeviceFamilies?: string[];
    sno?: number;
    isEnabled?: boolean;
}): Promise<MsgResult<{ id?: number; parentId?: number; categoryCode?: string; nameEn?: string; taskCode?: string }>> {
    return requestWithMsg(`${BASE}/material-categories`, { method: 'POST', body: payload });
}

export function updateMaterialCategory(
    id: number,
    payload: {
        nameZh?: string;
        nameEn?: string;
        icon?: string;
        codePrefix?: string;
        usageDocUrl?: string;
        usageDocTitle?: string;
        excludedDeviceFamilies?: string[];
        sno?: number;
        isEnabled?: boolean;
    },
): Promise<MsgResult<{ taskCode?: string } | null>> {
    return requestWithMsg(`${BASE}/material-categories/${id}`, { method: 'PATCH', body: payload });
}

/** 同级分组内全量排序 (parentId=0 为一级分组, 否则为该一级分类下的二级分组), 依序重写 sno=1..N. */
export function reorderMaterialCategories(
    parentId: number,
    ids: number[],
): Promise<MsgResult<{ count?: number; taskCode?: string }>> {
    return requestWithMsg(`${BASE}/material-categories-reorder`, {
        method: 'PUT',
        body: { parentId, ids },
    });
}

export function deleteMaterialCategory(id: number): Promise<MsgResult<{ taskCode?: string } | null>> {
    return requestWithMsg(`${BASE}/material-categories/delete`, { method: 'POST', body: { id } });
}
